/*
 * @Description: DatePicker组件，封装MUI的DatePicker组件
 */
'use client';

import { SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import { DateOutlined } from '@zeelool/icon';
import { Dayjs } from 'dayjs';
import React, { forwardRef, useState } from 'react';
import { Control, Controller, RegisterOptions } from 'react-hook-form';

// 关键是使用正确的泛型参数
export interface DatePickerProps extends Omit<MuiDatePickerProps<Dayjs>, 'renderInput'> {
  /**
   * 当前选中的日期
   */
  value?: Dayjs | null;
  /**
   * 日期变化时的回调函数
   */
  onChange?: (date: Dayjs | null) => void;
  /**
   * 用户接受日期选择时的回调函数
   */
  onAccept?: (date: Dayjs | null) => void;
  /**
   * 是否必填
   */
  required?: boolean;
  /**
   * 占位符
   */
  placeholder?: string;
  /**
   * 错误状态
   */
  error?: boolean;
  /**
   * 帮助文本
   */
  helperText?: string;
  /**
   * 日期格式
   */
  format?: string;
  /**
   * 输入框变体
   */
  variant?: 'outlined' | 'filled' | 'standard';
  /**
   * MUI样式对象
   */
  sx?: SxProps<Theme>;
  /**
   * 自定义输入框样式
   */
  inputSx?: SxProps<Theme>;
  /**
   * 日历打开时默认显示的月份，不会影响输入框的值
   */
  initialMonth?: Dayjs;
  /**
   * 是否在选择器打开时设置默认显示的月份
   */
  showInitialMonthOnOpen?: boolean;
  /**
   * react-hook-form 字段名
   */
  name?: string;
  /**
   * react-hook-form 控制器
   */
  control?: Control<any>;
  /**
   * react-hook-form 验证规则
   */
  rules?: RegisterOptions;
}

// 自定义DateOutlined组件，防止ownerState属性被传递到SVG上-
// https://github.com/mui/material-ui/issues/32882#issuecomment-1135784140
const CustomeDateOutlined = React.forwardRef((props, ref) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  // @ts-ignore: MUI internal props like ownerState are not in type definitions
  const { ownerState, componentsProps, style, ...other } = props;

  // @ts-ignore: Ref forwarding type mismatch with MUI DateOutlined component
  return (
    <DateOutlined
      {...other}
      ref={ref}
      style={{
        fontSize: isLargeScreen ? '20px' : '18px',

        ...style,
      }}
    />
  );
});

CustomeDateOutlined.displayName = 'CustomeDateOutlined';

/**
 * DatePicker组件，封装MUI的DatePicker组件
 */
const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>((props, ref) => {
  const {
    value,
    onChange,
    onAccept,
    required = false,
    // error = false,
    helperText,
    format = 'YYYY-MM-DD',
    placeholder,
    views = ['year', 'month', 'day'],
    variant = 'outlined',
    sx,
    inputSx,
    shouldDisableDate,
    initialMonth,
    showInitialMonthOnOpen = false,
    name,
    control,
    rules,
    ...otherProps
  } = props;

  // 用于控制DatePicker内部显示的日期，不影响实际选中的值
  const [openDate, setOpenDate] = useState<Dayjs | null>(initialMonth || null);

  // 处理日历打开事件
  const handleOpen = () => {
    if (showInitialMonthOnOpen && initialMonth) {
      setOpenDate(initialMonth);
    }
  };

  // 新增：如果没有传control和name，直接受控渲染MuiDatePicker
  if (!control && !name) {
    return (
      <MuiDatePicker
        ref={ref}
        value={value}
        onChange={onChange}
        onAccept={onAccept}
        format={format}
        views={views}
        shouldDisableDate={shouldDisableDate}
        onOpen={handleOpen}
        openTo="day"
        referenceDate={openDate}
        slots={{
          openPickerIcon: CustomeDateOutlined,
        }}
        sx={{
          '& .MuiInputBase-root': {
            height: '48px',
          },
          '& .MuiPickersDay-root': {
            borderRadius: '4px',
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: '#ffffff',
            },
            '&:hover': {
              backgroundColor: 'rgba(109, 103, 235, 0.1)',
            },
          },
          ...sx,
        }}
        slotProps={{
          textField: {
            variant,
            placeholder,
            required,
            helperText,
            fullWidth: true,
            sx: {
              '& .MuiInputAdornment-root': {
                paddingRight: '6px',
              },
              ...inputSx,
            },
          },
          day: {
            sx: {
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(109, 103, 235, 0.1)',
              },
            },
          },
        }}
        {...otherProps}
      />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={value}
      render={({ field, fieldState: { error: fieldError } }) => (
        <MuiDatePicker
          ref={ref}
          value={field.value}
          onChange={(date) => {
            field.onChange(date);
            if (onChange) onChange(date);
          }}
          onAccept={(date) => {
            if (onAccept) onAccept(date);
          }}
          format={format}
          views={views}
          shouldDisableDate={shouldDisableDate}
          onOpen={handleOpen}
          openTo="day"
          referenceDate={openDate}
          slots={{
            openPickerIcon: CustomeDateOutlined,
          }}
          sx={{
            '& .MuiInputBase-root': {
              height: '48px',
            },
            '& .MuiPickersDay-root': {
              borderRadius: '4px',
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: '#ffffff',
              },
              '&:hover': {
                backgroundColor: 'rgba(109, 103, 235, 0.1)',
              },
            },
            ...sx,
          }}
          slotProps={{
            textField: {
              variant,
              placeholder,
              required,
              error: !!fieldError,
              helperText: fieldError ? fieldError.message : helperText,
              fullWidth: true,
              sx: {
                '& .MuiInputAdornment-root': {
                  paddingRight: '6px',
                },
                ...inputSx,
              },
            },
            day: {
              sx: {
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(109, 103, 235, 0.1)',
                },
              },
            },
          }}
          {...otherProps}
        />
      )}
    />
  );
});

DatePicker.displayName = 'DatePicker';

export default DatePicker;
